// ─── GPU BUDGET FOR THE 3D VIEWS ─────────────────────────────────────────────
//
// Mobile browsers kill a tab that asks for too much GPU memory. The user does
// not see a WebGL error when that happens — they see the browser's own crash
// notice ("A problem occurred with this webpage so it was reloaded" on iOS
// Safari, "Something went wrong while displaying this webpage" on Android),
// which looks exactly like the page reloading itself.
//
// None of this cost is visible in the JS heap, which is why heap profiling of
// this bug came back clean: shadow maps, multisample buffers and the preserved
// drawing buffer all live in GPU memory.
//
// Measured on an emulated iPhone 13 against a 6-room project, the fullscreen
// room view held ~41 MB of textures and ~42 MB of renderbuffers — and the
// renderbuffer figure QUADRUPLED (10.5 → 42.2 MB) at the moment fullscreen
// opened, because the buffer scales with the drawing surface and multisampling
// multiplies it again. A real phone runs at a higher device pixel ratio than
// the emulator, so the true figure on the user's device is larger still.
//
// These helpers are functions, not constants, so they read the real viewport at
// the moment a Canvas is created rather than whatever it happened to be when
// the module was first imported.

/** True for phones/tablets — small viewport or a touch-primary pointer. */
export function isLowPowerGPU() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 1024
    || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
}

/**
 * Shadow-map edge in texels. The depth texture costs edge² × 4 bytes, so 4096²
 * is ~67 MB on its own — fine on a desktop GPU, and on a phone a large enough
 * slice of the whole budget to get the tab discarded. 1024² is 16× less and, at
 * the size a room is drawn on a phone screen, not visibly softer.
 */
export function shadowMapSize() {
  return isLowPowerGPU() ? 1024 : 4096;
}

/**
 * Whether to ask for multisample antialiasing.
 *
 * MSAA allocates a multisample colour buffer AND a multisample depth buffer at
 * the full drawing-buffer size, several samples deep — the single largest
 * allocation in the fullscreen view, and it grows exactly when the user goes
 * fullscreen. Phones already run at a device pixel ratio of 2–3, where the
 * pixels are far too small for the jaggies MSAA removes to be visible, so on
 * mobile this buys nothing and costs the most.
 */
export function useMSAA() {
  return !isLowPowerGPU();
}

/**
 * Sample count for the postprocessing composer's render target.
 *
 * This one matters more than every other setting here combined. An
 * <EffectComposer> with no `multisampling` prop defaults to 8 in
 * @react-three/postprocessing, and that allocates a render target the full size
 * of the drawing buffer, EIGHT samples deep. Measured on an emulated iPhone 13
 * that is 780×1688×4 bytes×8 ≈ 42 MB — the single largest allocation in the
 * scene, and it is re-allocated larger the moment the view goes fullscreen,
 * which is exactly when the user reports the crash.
 *
 * 0 disables multisampling on the composer only. The effects themselves (the
 * bloom glow around lights) still render, so the view keeps its look; it just
 * stops paying 8× for edge smoothing that is invisible at phone pixel density.
 */
export function postFxSamples() {
  return isLowPowerGPU() ? 0 : 8;
}
